import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@itsrever/design-system'
import {
    ModelsRefundPaymentMethod,
    PkgInfrastructureRestRefundMethod
} from '@itsrever/returns-api-types'
import { RefundMethodCard } from '@/app/refund-methods/refund-method-card'
import TopBar from '@/components/TopBarComponent/TopBar'
import { useCalculate, usePages, useReturnMethods, useTranslate } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { usePostHog } from '@/application/use-posthog'
import {
    FraudGroup,
    FraudGroups,
    FraudScore,
    FraudScores,
    RefundActions,
    RefundPaymentMethod,
    RefundPaymentMethods,
    ReturnMethod,
    setKeepYourItem,
    setRefundPaymentMethod
} from '@/redux/return/returnSlice'
import { LoadingModal } from '@/components/Loading'
import FallbackNotFound from '@/components/FallbackNotFound'

function isGiftCardOrPromoCode(method: PkgInfrastructureRestRefundMethod) {
    return (
        method.method ===
            ModelsRefundPaymentMethod.RefundPaymentMethod_GIFT_CARD ||
        method.method ===
            ModelsRefundPaymentMethod.RefundPaymentMethod_PROMO_CODE
    )
}

export const RefundMethodsPage = () => {
    const { translate } = useTranslate()

    const { captureEventPosthog } = usePostHog()

    const dispatch = useAppDispatch()

    const navigate = useNavigate()
    const { pages } = usePages()

    const { callCalculate, loadingCalculate } = useCalculate()
    const { callReturnMethods } = useReturnMethods(() =>
        navigate(pages.ReturnMethod)
    )

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const orderReturn = useAppSelector((store) => store.return)

    const refundMethodsResponse = useAppSelector(
        (store) => store.refundMethodsApi.refundMethods.response
    )

    const refundMethods = refundMethodsResponse.result?.refund_methods

    const isGiftCardOffered =
        refundMethods?.some(
            ({ method }) =>
                method ===
                    ModelsRefundPaymentMethod.RefundPaymentMethod_GIFT_CARD ||
                method ===
                    ModelsRefundPaymentMethod.RefundPaymentMethod_PROMO_CODE
        ) ?? false

    const handleContinue = () => {
        captureEventPosthog('Refund Method', {
            RefundPaymentMethod:
                RefundPaymentMethods[
                    orderReturn.refundPaymentMethod.method ??
                        RefundPaymentMethod.NoPaymentMethod
                ],
            FraudScore:
                FraudScores[
                    refundMethodsResponse.result?.fraud_result
                        ?.outcome as FraudScore
                ],
            FraudGroup:
                FraudGroups[
                    refundMethodsResponse.result?.fraud_result
                        ?.group as FraudGroup
                ],
            ecommerceId: settings.ecommerce_id
        })

        const notReceivedOnly = orderReturn.returnedItems.every(
            (retItem) =>
                retItem.item.product_return_reason == 'NOT_RECEIVED' ||
                retItem.item.product_return_reason == 'PACKAGE_NOT_ARRIVED'
        )

        const hasExchanges = orderReturn.returnedItems.some(
            (item) => item.item.action === RefundActions.ToExchange
        )

        if (notReceivedOnly) {
            if (hasExchanges) {
                navigate(pages.ExchangeAddress)
            } else {
                callCalculate(ReturnMethod.NoReturnMethod)
            }
            return
        } else {
            callReturnMethods(orderReturn.returnAddress)
        }
    }

    useEffect(() => {
        if (refundMethodsResponse.result?.fraud_result) {
            captureEventPosthog('Fraud Score Result', {
                FraudScore:
                    FraudScores[
                        refundMethodsResponse.result.fraud_result
                            .outcome as FraudScore
                    ],
                FraudGroup:
                    FraudGroups[
                        refundMethodsResponse.result.fraud_result
                            .group as FraudGroup
                    ],
                ecommerceId: settings.ecommerce_id
            })
        }

        if (refundMethods && settings.ecommerce_id) {
            captureEventPosthog('Refund Method Page Loaded', {
                RefundMethodsOffered: refundMethods?.map(
                    (method) => method.method ?? 0
                ),
                GiftCardOffered: isGiftCardOffered,
                ecommerceId: settings.ecommerce_id,
                GiftCardRDVSurplus: isGiftCardOffered
                    ? refundMethods.some(
                          (method) =>
                              isGiftCardOrPromoCode(method) &&
                              ((method.rdv_surplus?.surplus_amount ?? 0) > 0 ||
                                  (method.rdv_surplus?.surplus_percentage ??
                                      0) > 0)
                      )
                    : false,
                GiftCardFreeShipping: isGiftCardOffered
                    ? refundMethods.some(
                          (method) =>
                              isGiftCardOrPromoCode(method) &&
                              parseFloat(
                                  method.shipping_costs?.max?.amount || '0'
                              ) === 0
                      )
                    : false,
                isKeepYourItem: orderReturn.keepYourItem
            })
        }
    }, [
        captureEventPosthog,
        refundMethodsResponse.result?.fraud_result,
        settings.ecommerce_id,
        isGiftCardOffered,
        refundMethods,
        orderReturn.keepYourItem
    ])

    return (
        <>
            {loadingCalculate ? <LoadingModal loading /> : null}
            <div className="flex-1 bg-white lg:bg-[#f2f2f2]">
                <TopBar page="refund-method" />
                <main className="flex items-center justify-center pb-3 pt-6">
                    <div className="relative size-full max-w-lg rounded-lg bg-white lg:px-8 lg:py-6 lg:shadow-md">
                        <div className="px-4 md:px-0">
                            <h1
                                className="text-center font-[Roobert] text-lg font-normal text-neutral-800"
                                dangerouslySetInnerHTML={{
                                    __html: translate('refund_page_title')
                                }}
                            />
                            {refundMethods && refundMethods.length > 0 ? (
                                <ul className="pb-6 pt-8">
                                    <div className="flex flex-col gap-4">
                                        {refundMethods?.map((refundMethod) => {
                                            if (refundMethod) {
                                                return (
                                                    <li
                                                        key={
                                                            refundMethod.method
                                                        }
                                                        onClick={() => {
                                                            dispatch(
                                                                setRefundPaymentMethod(
                                                                    refundMethod
                                                                )
                                                            )
                                                            dispatch(
                                                                setKeepYourItem(
                                                                    false
                                                                )
                                                            )
                                                        }}
                                                    >
                                                        <RefundMethodCard
                                                            method={
                                                                refundMethod
                                                            }
                                                            isSelected={
                                                                refundMethod.method ===
                                                                orderReturn
                                                                    .refundPaymentMethod
                                                                    .method
                                                            }
                                                            isGiftCardOffered={
                                                                isGiftCardOffered
                                                            }
                                                        />
                                                    </li>
                                                )
                                            }

                                            return null
                                        })}
                                    </div>
                                </ul>
                            ) : (
                                <div className="pt-6">
                                    <FallbackNotFound
                                        title={translate(
                                            'refund_page_no_methods_available'
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="sticky bottom-0 flex w-full justify-center bg-white px-4 py-2.5 md:relative md:p-0">
                            <div className="max-w-80 flex-1">
                                <Button
                                    fullWidth
                                    onClick={handleContinue}
                                    disabled={
                                        orderReturn.refundPaymentMethod
                                            .method === 0 ||
                                        refundMethods?.length === 0
                                    }
                                >
                                    {translate('refund_page_button_continue')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    )
}
